import {
  ActionIcon,
  Autocomplete,
  Box,
  Button,
  Card,
  Flex,
  SegmentedControl,
  Switch,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useCalendarContext } from "../../../../contexts/CalendarContext";
import { IconCheck, IconSearch, IconX } from "@tabler/icons-react";
import { CALENDAR_TYPES } from "../../../../constants/calendarConstants";
import { COLORS } from "../../../../constants/colorConstants";
import { useLocalAuthorityContext } from "../../../../contexts/LocalAuthorityContext";
import { useMemo } from "react";
import { mapLocalAuthoritiesGroupedByRegion } from "../../../../utils/mappingUtils";

export default function CalendarTableFilters() {
  const { tableFetch } = useCalendarContext();

  return (
    <Card shadow="xs" p="md" radius="lg" mb="md" withBorder bg={COLORS.DARK_BROWN} c={COLORS.WHEAT}>
      <Flex align="center" gap="md">
        <Search tableFetch={tableFetch} />
        <ActiveFilter tableFetch={tableFetch} />
        <TypeFilter tableFetch={tableFetch} />
        <LocalAuthorityFilter tableFetch={tableFetch} />
        <ClearFilters tableFetch={tableFetch} />
      </Flex>
    </Card>
  );
}
function Search({ tableFetch }) {
  return (
    <Tooltip label="Search by calendar ID or name" position="bottom" withArrow>
      <Box style={{ "--mantine-color-placeholder": tableFetch.tableFilters.search ? COLORS.DARK_BROWN : COLORS.MOCHA }}>
        <TextInput
          placeholder="Search calendars..."
          styles={{
            input: {
              backgroundColor: COLORS.WHEAT,
              color: COLORS.DARK_BROWN,
            },
          }}
          w="250px"
          variant="filled"
          radius="xl"
          fw={700}
          leftSection={<IconSearch color={COLORS.DARK_BROWN} size={16} />}
          rightSection={
            tableFetch.tableFilters.search ? (
              <ActionIcon
                variant="filled"
                color={COLORS.DARK_BROWN}
                size="sm"
                radius="xl"
                onClick={() => tableFetch.updateTableFilters({ search: "" })}
              >
                <IconX size={16} color={COLORS.WHEAT} />
              </ActionIcon>
            ) : null
          }
          value={tableFetch.tableFilters.search}
          onChange={(e) => tableFetch.updateTableFilters({ search: e.currentTarget.value })}
        />
      </Box>
    </Tooltip>
  );
}

function ActiveFilter({ tableFetch }) {
  return (
    <Switch
      defaultChecked
      color={COLORS.WHEAT}
      onLabel={
        <span
          style={{
            color: COLORS.DARK_BROWN,
            fontSize: 16,
            fontWeight: 700,
            padding: "0px 4px 0px 4px",
          }}
        >
          Active
        </span>
      }
      offLabel={
        <span
          style={{
            color: COLORS.DARK_BROWN,
            fontSize: 16,
            fontWeight: 700,
            padding: "0px 4px 0px 4px",
          }}
        >
          Inactive
        </span>
      }
      size="xl"
      thumbIcon={
        tableFetch.tableFilters.isActive ? (
          <IconCheck color={COLORS.WHEAT} size={12} stroke={3} />
        ) : (
          <IconX color={COLORS.WHEAT} size={12} stroke={3} />
        )
      }
      styles={{
        thumb: {
          backgroundColor: COLORS.DARK_BROWN,
        },
      }}
      checked={!!tableFetch.tableFilters.isActive}
      onChange={(e) => tableFetch.updateTableFilters({ isActive: e.currentTarget.checked })}
    />
  );
}

function TypeFilter({ tableFetch }) {
  const data = [
    { label: "All", value: "" },
    { label: CALENDAR_TYPES.EDUCATION.label, value: CALENDAR_TYPES.EDUCATION.id },
    { label: CALENDAR_TYPES.HOLIDAY.label, value: CALENDAR_TYPES.HOLIDAY.id },
  ].map((item) => ({
    value: item.value,
    label: (
      <span
        style={{
          color: item.value === tableFetch.tableFilters.type ? COLORS.WHEAT : COLORS.DARK_BROWN,
          fontWeight: 700,
        }}
      >
        {item.label}
      </span>
    ),
  }));

  return (
    <SegmentedControl
      bg={COLORS.WHEAT}
      color={COLORS.DARK_BROWN}
      size="sm"
      radius="xl"
      transitionDuration={200}
      data={data}
      value={tableFetch.tableFilters.type}
      onChange={(value) => tableFetch.updateTableFilters({ type: value })}
    />
  );
}

function LocalAuthorityFilter({ tableFetch }) {
  const { dropdownFetch } = useLocalAuthorityContext();

  const localAuthorities = dropdownFetch?.data ?? [];
  const data = useMemo(() => {
    return mapLocalAuthoritiesGroupedByRegion(localAuthorities);
  }, [localAuthorities]);

  return (
    <Box
      style={{
        "--mantine-color-placeholder": tableFetch.tableFilters.localAuthorityId ? COLORS.DARK_BROWN : COLORS.MOCHA,
      }}
    >
      <Autocomplete
        placeholder="Select local authority..."
        size="sm"
        radius="xl"
        color={COLORS.DARK_BROWN}
        c={COLORS.WHEAT}
        fw={700}
        maxDropdownHeight={200}
        w={400}
        clearable
        styles={{
          input: {
            backgroundColor: COLORS.WHEAT,
          },
          dropdown: {
            backgroundColor: COLORS.WHEAT,
            color: COLORS.DARK_BROWN,
            fontWeight: 700,
          },
          groupLabel: {
            color: COLORS.MOCHA,
          },
        }}
        clearButtonProps={{
          c: COLORS.WHEAT,
          bg: COLORS.DARK_BROWN,
          radius: "xl",
          variant: "light",
        }}
        comboboxProps={{ transitionProps: { transition: "fade", duration: 200 } }}
        data={data}
        value={tableFetch.tableFilters.localAuthorityId}
        onChange={(value) => tableFetch.updateTableFilters({ localAuthorityId: value })}
      />
    </Box>
  );
}

function ClearFilters({ tableFetch }) {
  const defaultFilters = { search: "", isActive: true, type: "", localAuthorityId: "" };
  const isDisabled = JSON.stringify(tableFetch.tableFilters) === JSON.stringify(defaultFilters);

  return (
    <Button
      size="sm"
      radius="lg"
      bg={COLORS.BRONZE}
      c={COLORS.WHEAT}
      disabled={isDisabled}
      fw={700}
      onClick={() => tableFetch.updateTableFilters(defaultFilters)}
    >
      Clear
    </Button>
  );
}
